import { useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Volume2, VolumeX, ChevronLeft, ChevronRight, Check } from 'lucide-react';
import TanoCharacter from '../../components/Eleve/TanoCharacter';
import { renderInline } from '../../components/VueQuestion/coupDePouceTexte';

/**
 * Bottom-sheet du coup de pouce — mini-cours paginé, 4 pages MAXIMUM :
 *   page 1 « La règle »    : la propriété (+ figure SVG optionnelle)
 *   page 2 « Un exemple »  : le même point traité sur d'autres valeurs
 *   page 3 (optionnelle)   : un point disjoint qui exige son propre support
 *   page 4 (optionnelle)   : réservée aux aides « un cas par page »
 *
 * Contrainte pédagogique portée par le format : si l'aide ne tient pas,
 * c'est l'aide qu'il faut réécrire, pas la sheet qu'il faut agrandir. Le
 * plafond est passé de 2 à 3 (prof, 2026-08-22, fiche 1033 : « une idée par
 * page » — trois relations, trois supports distincts), puis de 3 à 4 (prof,
 * 2026-09-09, fiche 1052 : quatre cas de comparaison, une page chacun).
 * Les pages sont nommées dans le pied par des PUCES portant leur titre, si
 * bien que l'élève saute directement à son cas au lieu de feuilleter (même
 * décision). Non bloquante (aucun backdrop) : la question reste manipulable.
 *
 * Emphase gérée par **texte** → <strong>, jamais de HTML brut : le contenu des
 * coups de pouce reste du texte, lisible tel quel par la synthèse vocale.
 */

const AMBRE = '#FFB938';
const AMBRE_SOMBRE = '#7c4a03';

// `renderInline` (**gras**, ==rouge==, $KaTeX$) vit désormais dans
// components/VueQuestion/coupDePouceTexte.jsx : la planche de cartes à
// découper de l'éval papier l'affiche aussi. Déplacement verbatim, aucun
// changement de rendu ici.

/** Retire le balisage pour la lecture audio ($\dfrac{a}{b}$ → « a sur b »). */
const plain = (texte) => String(texte)
    .replace(/\$([^$]*)\$/g, (m, tex) => tex
        .replace(/\\d?frac\{([^}]*)\}\{([^}]*)\}/g, '$1 sur $2')
        .replace(/\\[a-zA-Z]+/g, ' ')
        .replace(/[{}]/g, ''))
    .replace(/\*\*/g, '')
    .replace(/==/g, '');

const CONSEIL_GENERIQUE = 'Relis bien l\'énoncé et élimine d\'abord les réponses impossibles !';

/**
 * Mode PAS À PAS des figures animées (demande utilisateur 2026-08-01).
 * Convention côté SVG : la racine porte data-etapes="N" ; chaque temps du
 * dessin porte data-etape="K" (affichage CUMULATIF 1..K) ; chaque sous-titre
 * porte data-caption="K" (affichage EXCLUSIF du temps courant). Le CSS
 * ci-dessous pilote l'intérieur du SVG injecté (même DOM) ; les figures sans
 * data-etapes gardent leur animation en boucle, rien ne change pour elles.
 */
const MAX_ETAPES = 12;
const CSS_PAS_A_PAS = (() => {
    const regles = ['.cp-pas [data-etape], .cp-pas [data-caption] { animation: none !important; opacity: 0 !important; }'];
    // Groupes `data-anim` (opt-in, gen_1023 2026-08-21) : cachés en
    // display:none au lieu d'opacity, si bien que l'animation CSS de leurs
    // ENFANTS (déclarée dans le <style> du SVG, jamais suppressée par la
    // règle ci-dessus qui ne vise que le groupe) REDÉMARRE à chaque entrée
    // dans leur étape — le compas glisse au clic de la flèche. Aucune fiche
    // existante ne porte cet attribut : purement additif.
    regles.push('.cp-pas [data-anim] { display: none !important; }');
    for (let n = 1; n <= MAX_ETAPES; n += 1) {
        const cumul = [];
        const cumulAnim = [];
        for (let k = 1; k <= n; k += 1) {
            cumul.push(`.cp-step-${n} [data-etape="${k}"]`);
            cumulAnim.push(`.cp-step-${n} [data-etape="${k}"][data-anim]`);
        }
        regles.push(`${cumul.join(', ')} { opacity: 1 !important; }`);
        regles.push(`${cumulAnim.join(', ')} { display: inline !important; }`);
        regles.push(`.cp-step-${n} [data-caption="${n}"] { opacity: 1 !important; }`);
    }
    return regles.join('\n');
})();

/**
 * Normalise la valeur renvoyée par getIndice en 1 à 4 pages.
 * Accepte, par ordre de priorité :
 *   { pages: [{ titre, lignes?, svg? }, …] }  → forme canonique, 4 pages max
 *   { court, svg?, exemple? }                 → forme courte historique
 *   'phrase' | null                           → page unique / conseil générique
 */
const buildPages = (brut) => {
    if (Array.isArray(brut?.pages) && brut.pages.length) {
        return { pages: brut.pages.slice(0, 4), placeholder: false };
    }

    const court = (typeof brut === 'string' ? brut : brut?.court) || CONSEIL_GENERIQUE;
    const svg = typeof brut === 'object' ? brut?.svg : null;
    const brutExemple = typeof brut === 'object' ? brut?.exemple : null;
    const lignes = Array.isArray(brutExemple)
        ? brutExemple
        : (brutExemple ? [brutExemple] : []);

    const pages = [{ titre: 'La règle', court, svg }];
    if (lignes.length) pages.push({ titre: 'Un exemple', lignes });
    return { pages, placeholder: !brut };
};

function CoupDePouceSheet({ indice, skins, onClose, xpPlein, xpAide, zIndex = 10012, showXp = true }) {
    const [page, setPage] = useState(0);
    const [speaking, setSpeaking] = useState(false);
    const [etape, setEtape] = useState(1);
    const [autoAnim, setAutoAnim] = useState(false);
    const touchX = useRef(null);

    const { pages, placeholder } = useMemo(() => buildPages(indice), [indice]);
    const nbPages = pages.length;
    const courante = pages[Math.min(page, nbPages - 1)];
    const derniere = page >= nbPages - 1;

    // Figure pilotable pas à pas ? (convention data-etapes, cf. CSS_PAS_A_PAS)
    const nbEtapes = useMemo(() => {
        const m = String(courante.svg || '').match(/data-etapes="(\d+)"/);
        return m ? Math.min(parseInt(m[1], 10), MAX_ETAPES) : 0;
    }, [courante]);
    // Le bouton « ▶ Animation » n'a de sens que si la figure embarque du SMIL :
    // une figure à étapes purement statique (620-627, 1006...) ne montrerait
    // qu'une image figée complète (décision 2026-08-20, retrait cas par cas).
    const aAnimation = useMemo(() => String(courante.svg || '').includes('<animate'), [courante]);
    const pasAPas = nbEtapes > 0 && !autoAnim;

    // Texte lisible à voix haute : le texte de la page + la narration de la
    // figure (son aria-label) pour les pages qui ne portent qu'un schéma.
    const texteALire = useMemo(() => {
        const morceaux = [courante.court, ...(courante.lignes || [])].filter(Boolean).map(plain);
        const mSvg = String(courante.svg || '').match(/aria-label="([^"]*)"/);
        if (mSvg) morceaux.push(mSvg[1]);
        return morceaux.join('. ');
    }, [courante]);

    const ttsSupported = typeof window !== 'undefined' && 'speechSynthesis' in window
        && Boolean(texteALire);

    /** Lit UNIQUEMENT la page affichée (l'élève entend ce qu'il voit). */
    const lirePage = () => {
        if (!ttsSupported) return;
        const synth = window.speechSynthesis;
        if (synth.speaking) { synth.cancel(); setSpeaking(false); return; }
        const u = new SpeechSynthesisUtterance(texteALire);
        u.lang = 'fr-FR';
        u.onend = () => setSpeaking(false);
        u.onerror = () => setSpeaking(false);
        synth.cancel();
        synth.speak(u);
        setSpeaking(true);
    };

    const allerA = (i) => {
        if (i < 0 || i >= nbPages) return;
        // Test direct : `ttsSupported` est faux sur une page sans texte, or il faut
        // quand même couper une lecture lancée depuis la page précédente.
        if (typeof window !== 'undefined' && 'speechSynthesis' in window && window.speechSynthesis.speaking) {
            window.speechSynthesis.cancel();
            setSpeaking(false);
        }
        setPage(i);
        setEtape(1);
        setAutoAnim(false);
    };

    const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
    const onTouchEnd = (e) => {
        if (touchX.current === null) return;
        const delta = e.changedTouches[0].clientX - touchX.current;
        if (Math.abs(delta) > 45) allerA(page + (delta < 0 ? 1 : -1));
        touchX.current = null;
    };

    return createPortal(
        <div className="eleve-gamified fixed inset-x-0 bottom-0 xp-sheet pointer-events-none" style={{ zIndex }}>
            <style>{CSS_PAS_A_PAS}</style>
            <div
                role="dialog"
                aria-label="Coup de pouce"
                onTouchStart={onTouchStart}
                onTouchEnd={onTouchEnd}
                className="mx-auto w-full max-w-xl sm:max-w-2xl rounded-t-3xl shadow-2xl bg-white flex flex-col pointer-events-auto"
                style={{ maxHeight: '70vh', borderTop: `4px solid ${AMBRE}` }}
            >
                {/* ── En-tête : Tano, titre de page, compteur, audio, fermer ── */}
                <div
                    className="flex items-center gap-2.5 px-4 pt-2.5 pb-2 flex-shrink-0"
                    style={{ background: '#FFF7E6', borderRadius: '20px 20px 0 0' }}
                >
                    <div className="flex-shrink-0" style={{ width: 46, height: 46 }}>
                        <TanoCharacter {...skins} mood="joy" size={46} />
                    </div>
                    <div className="min-w-0 flex-1">
                        <p className="font-extrabold text-sm truncate" style={{ fontFamily: 'Fredoka', color: AMBRE_SOMBRE }}>
                            {courante.titre}
                        </p>
                        {nbPages > 1 && (
                            <p className="text-[10px] font-bold" style={{ color: 'rgba(124,74,3,.6)' }}>
                                Étape {page + 1} sur {nbPages}
                            </p>
                        )}
                    </div>
                    {ttsSupported && (
                        <button
                            type="button"
                            onClick={lirePage}
                            aria-label={speaking ? 'Arrêter la lecture' : 'Écouter cette étape'}
                            className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition hover:opacity-80"
                            style={{ background: speaking ? '#F97316' : 'rgba(249,115,22,.15)', color: speaking ? '#fff' : '#b45309' }}
                        >
                            {speaking ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Fermer le coup de pouce"
                        className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition hover:bg-black/5"
                        style={{ color: '#b07514' }}
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {/* ── Corps : la page courante ── */}
                <div key={page} className="cp-bulle px-4 py-3 overflow-y-auto flex-1">
                    {/* Une page porte du texte, une figure, ou les deux. */}
                    {courante.court && (
                        <p className="text-[15px] font-bold leading-snug" style={{ color: '#3b2303' }}>
                            💡 {renderInline(courante.court)}
                        </p>
                    )}
                    {courante.lignes && (
                        <ul className={`space-y-2 ${courante.court ? 'mt-3' : ''}`}>
                            {courante.lignes.map((l, i) => (
                                <li
                                    key={i}
                                    className="flex gap-2.5 items-start rounded-xl px-3 py-2 text-[15px] leading-relaxed"
                                    style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', color: '#334155' }}
                                >
                                    <span
                                        className="flex-shrink-0 mt-px w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-extrabold"
                                        style={{ background: AMBRE, color: '#3b2303' }}
                                    >
                                        {i + 1}
                                    </span>
                                    <span>{renderInline(l)}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                    {courante.svg && (
                        <div
                            className={`rounded-xl p-2 flex justify-center ${courante.court || courante.lignes ? 'mt-3' : ''} ${pasAPas ? `cp-pas cp-step-${etape}` : ''}`}
                            style={{ background: '#F8FAFC', border: '1px solid #E2E8F0' }}
                            // SVG statique écrit en dur dans src/generators/coupsDePouce, jamais de saisie utilisateur.
                            dangerouslySetInnerHTML={{ __html: courante.svg }}
                        />
                    )}
                    {courante.svg && nbEtapes > 0 && (
                        <div className="flex items-center justify-center gap-2 mt-2">
                            {autoAnim ? (
                                <button
                                    type="button"
                                    onClick={() => { setAutoAnim(false); setEtape(1); }}
                                    className="px-3 h-7 rounded-full text-[11px] font-extrabold transition active:scale-95"
                                    style={{ fontFamily: 'Fredoka', background: 'rgba(249,115,22,.12)', color: '#b45309' }}
                                >
                                    ⏸ Revenir au pas à pas
                                </button>
                            ) : (
                                <>
                                    <button
                                        type="button"
                                        onClick={() => setEtape((e) => Math.max(1, e - 1))}
                                        disabled={etape === 1}
                                        aria-label="Étape précédente"
                                        className="w-7 h-7 rounded-full flex items-center justify-center transition disabled:opacity-25"
                                        style={{ background: 'rgba(249,115,22,.12)', color: '#b45309' }}
                                    >
                                        <ChevronLeft className="w-4 h-4" />
                                    </button>
                                    <span className="text-[11px] font-extrabold tabular-nums" style={{ color: AMBRE_SOMBRE }}>
                                        Étape {etape} / {nbEtapes}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => setEtape((e) => Math.min(nbEtapes, e + 1))}
                                        disabled={etape === nbEtapes}
                                        aria-label="Étape suivante"
                                        className="w-7 h-7 rounded-full flex items-center justify-center transition disabled:opacity-25"
                                        style={{ background: etape === nbEtapes ? 'rgba(249,115,22,.12)' : AMBRE, color: etape === nbEtapes ? '#b45309' : '#3b2303' }}
                                    >
                                        <ChevronRight className="w-4 h-4" />
                                    </button>
                                    {aAnimation ? (
                                        <button
                                            type="button"
                                            onClick={() => setAutoAnim(true)}
                                            className="ml-2 px-3 h-7 rounded-full text-[11px] font-extrabold transition active:scale-95"
                                            style={{ fontFamily: 'Fredoka', background: 'rgba(249,115,22,.12)', color: '#b45309' }}
                                        >
                                            ▶ Animation
                                        </button>
                                    ) : null}
                                </>
                            )}
                        </div>
                    )}
                    {placeholder && (
                        <p className="mt-2 text-[10px] font-semibold" style={{ color: 'rgba(124,74,3,.5)' }}>
                            ✨ Le mini-cours de cet exercice arrive bientôt
                        </p>
                    )}
                </div>

                {/* ── Pied : navigation + barème XP ── */}
                <div
                    className="flex items-center gap-3 px-4 py-2.5 flex-shrink-0"
                    style={{ borderTop: '1px solid #F1F5F9', background: '#FFFDF7' }}
                >
                    {nbPages > 1 && (
                        <button
                            type="button"
                            onClick={() => allerA(page - 1)}
                            disabled={page === 0}
                            aria-label="Étape précédente"
                            className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition disabled:opacity-25"
                            style={{ background: 'rgba(249,115,22,.12)', color: '#b45309' }}
                        >
                            <ChevronLeft className="w-4 h-4" />
                        </button>
                    )}

                    {/* Puces nommées : chaque page par son titre, l'élève saute à son cas
                        sans feuilleter (quatre puces se replient sur deux lignes au téléphone). */}
                    <div className="flex flex-wrap items-center gap-1.5 flex-1 justify-center">
                        {nbPages > 1 && pages.map((p, i) => (
                            <button
                                key={p.titre}
                                type="button"
                                onClick={() => allerA(i)}
                                aria-label={`Aller à : ${p.titre}`}
                                aria-current={i === page}
                                className="px-2.5 h-7 rounded-full text-[11px] font-extrabold transition-all active:scale-95"
                                style={{
                                    fontFamily: 'Fredoka',
                                    background: i === page ? AMBRE : 'rgba(249,115,22,.12)',
                                    color: i === page ? '#3b2303' : '#b45309',
                                }}
                            >
                                {p.titre}
                            </button>
                        ))}
                    </div>

                    {derniere ? (
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-shrink-0 flex items-center gap-1.5 px-3.5 h-8 rounded-full text-xs font-extrabold transition active:scale-95"
                            style={{ fontFamily: 'Fredoka', background: AMBRE, color: '#3b2303' }}
                        >
                            <Check className="w-3.5 h-3.5" /> J'ai compris
                        </button>
                    ) : (
                        <button
                            type="button"
                            onClick={() => allerA(page + 1)}
                            aria-label="Étape suivante"
                            className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition active:scale-95"
                            style={{ background: AMBRE, color: '#3b2303' }}
                        >
                            <ChevronRight className="w-4 h-4" />
                        </button>
                    )}
                </div>

                {/* Barème masqué hors passation (aperçu prof : aucun XP en jeu). */}
                {showXp && (
                    <p className="px-4 pb-2 text-[10px] font-semibold flex-shrink-0" style={{ color: 'rgba(124,74,3,.7)' }}>
                        Avec un coup de pouce, une bonne réponse rapporte +{xpAide} XP au lieu de +{xpPlein}. Tu peux revoir cette aide quand tu veux.
                    </p>
                )}
            </div>
        </div>,
        document.body,
    );
}

export default CoupDePouceSheet;
